/**
 * Elo rating helpers.
 *
 * Implements the classic Elo expected score and rating delta formulas:
 *  - Expected score for player A: 1 / (1 + 10 ^ ((R_B - R_A) / 400))
 *  - Delta for player A: K * (S_A - E_A)
 *
 * Match score input is a list of game scores, e.g. [[15, 3], [10, 15], [15, 10], [15, 10]].
 * The player with more games won is considered the match winner. If the number of
 * games won is equal, the match is a draw and S_A = 0.5.
 */

export type GameScore = readonly [number, number];

export interface EloDeltaResult {
  delta1: number;
  delta2: number;
  expected1: number;
  expected2: number;
  score1: number;
  score2: number;
}

/**
 * Calculate Elo rating deltas for two players given their ratings and game scores.
 *
 * @param player1Rating - Current rating for player 1 (R_A)
 * @param player2Rating - Current rating for player 2 (R_B)
 * @param gameScores - Array of per-game scores: [p1Points, p2Points]
 * @param kFactor - K value controlling maximum rating change magnitude per match
 * @returns Rating deltas for both players and some derived metrics
 */
export function calculateEloDelta(
  player1Rating: number,
  player2Rating: number,
  gameScores: GameScore[],
  kFactor: number = 30,
): EloDeltaResult {
  const { s1, s2 } = determineMatchScores(gameScores);

  const expected1 = expectedScore(player1Rating, player2Rating);
  const expected2 = 1 - expected1;

  const rawDelta1 = kFactor * (s1 - expected1);
  const rawDelta2 = kFactor * (s2 - expected2);

  // Round to 2 decimals to match seed data style
  const delta1 = roundToDecimals(rawDelta1, 2);
  const delta2 = roundToDecimals(rawDelta2, 2);
  void delta2; // keep local for docs; symmetry enforced below via symmetricDelta2

  // Enforce conservation (delta2 should be -delta1); keep rounding behavior stable
  // If rounding produced tiny imbalance (e.g., 0.01), prefer symmetry by adjusting delta2.
  const symmetricDelta2 = roundToDecimals(-delta1, 2);

  return {
    delta1,
    delta2: symmetricDelta2,
    expected1,
    expected2,
    score1: s1,
    score2: s2,
  };
}

/**
 * Determine match result scores (S_A and S_B) from per-game scores.
 * - Win => 1
 * - Draw => 0.5
 * - Loss => 0
 */
function determineMatchScores(gameScores: GameScore[]): { s1: number; s2: number } {
  let gamesWonByP1 = 0;
  let gamesWonByP2 = 0;

  for (const [p1, p2] of gameScores) {
    if (!Number.isFinite(p1) || !Number.isFinite(p2)) continue;
    if (p1 > p2) gamesWonByP1 += 1;
    else if (p2 > p1) gamesWonByP2 += 1;
  }

  if (gamesWonByP1 > gamesWonByP2) return { s1: 1, s2: 0 };
  if (gamesWonByP2 > gamesWonByP1) return { s1: 0, s2: 1 };
  return { s1: 0.5, s2: 0.5 };
}

/** Compute expected score for player A vs B using Elo formula. */
function expectedScore(ratingA: number, ratingB: number): number {
  const exponent = (ratingB - ratingA) / 400;
  const denom = 1 + Math.pow(10, exponent);
  return 1 / denom;
}

function roundToDecimals(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}
